"use client";

import type { LeadFormSurface } from "@homeinstead/analytics";
import type {
  LeadFlowBlockPresentation,
  LeadFlowSectionTakeover,
} from "../types";

import { useCallback, useState } from "react";

type UseLeadFlowSurfaceInput = {
  currentStepIndex: number;
  isShowingThankYou: boolean;
  presentation: LeadFlowBlockPresentation;
  sectionTakeover: LeadFlowSectionTakeover | undefined;
  stepCount: number;
};

/**
 * Where the flow renders right now. The first step renders in the section's
 * content slot, or behind the launcher's call to action. Later steps, and a
 * launcher's opened flow, render on the block's surface. An inline flow that
 * takes over its section swaps steps in place instead.
 */
function useSurface({
  currentStepIndex,
  isShowingThankYou,
  presentation,
  sectionTakeover,
  stepCount,
}: UseLeadFlowSurfaceInput) {
  const [isLauncherOpen, setIsLauncherOpen] = useState(false);
  const { entry, surface } = presentation;
  // The thank-you replaces the last step, where the visitor submitted, so it
  // renders where that step did even if the visitor went back to the first
  // step while the submit was pending.
  const isPastFirstStep = isShowingThankYou
    ? stepCount > 1
    : currentStepIndex > 0;
  const isOpen = entry === "inline" || isLauncherOpen || isPastFirstStep;
  const dialogSurface =
    surface !== "inline" && (entry === "launcher" ? isOpen : isPastFirstStep)
      ? surface
      : null;
  const isOnSurface = dialogSurface !== null;
  const renderedSurface: LeadFormSurface = dialogSurface ?? "inline";
  const isInContentSlot = !isOnSurface && currentStepIndex === 0;
  const takesOverSection = sectionTakeover !== undefined;
  // Only a flow that stays inline swaps steps in place; on a surface the
  // section keeps the first step for the whole flow.
  const isTakingOverSection =
    takesOverSection &&
    entry === "inline" &&
    surface === "inline" &&
    isPastFirstStep;

  const openLauncher = useCallback(() => setIsLauncherOpen(true), []);
  const closeLauncher = useCallback(() => setIsLauncherOpen(false), []);

  return {
    closeLauncher,
    /** The dialog the flow renders in, or null while it is in the page. */
    dialogSurface,
    isInContentSlot,
    isOnSurface,
    isOpen,
    isTakingOverSection,
    openLauncher,
    renderedSurface,
    takesOverSection,
  };
}

export { useSurface };
